import { createStore } from '../state/createStore';

// Typed stack navigation — its own module from day one, never woven into a screen file.
//
// Navigation identity: FOUR bottom tabs, 首页 (Home) · 验屋 (Inspections) · 报告 (Reports) · 设置
// (Settings). Owner task book 2026-09-04 §19 had three; the Owner added Home on 2026-09-11 after
// TestFlight 2.0.0 (84): 「这个就是一个验屋页。要有一个像样的首页。」 Areas / Findings / Sync are
// still NOT tabs: areas and findings are work inside an inspection, sync is a status page under
// Settings. Tab ids are route identity and never change with the App Language.
// Deep field screens (area, item, finding, AI scan, photo pool, bulk import, closeout, the new
// inspection form) do not show the global tab bar.
export type TabId = 'home' | 'inspections' | 'reports' | 'settings';

export const TAB_ORDER: readonly TabId[] = Object.freeze(['home', 'inspections', 'reports', 'settings']);

// 'profile' (NY F1 Round 1) is the inspector's own licence record: entered once, copied onto
// every new inspection. It is a Settings page, not a new tab — the shell keeps three identities.
export type SettingsPage = 'language' | 'profile' | 'report' | 'comments' | 'account' | 'sync' | 'photos' | 'plan' | 'help' | 'privacy' | 'terms' | 'about';

export const SETTINGS_PAGE_ORDER: readonly SettingsPage[] = Object.freeze(['language', 'profile', 'report', 'comments', 'account', 'sync', 'photos', 'plan', 'help', 'privacy', 'terms', 'about']);

export type PoolFocus = { kind: 'area'; areaId: string } | { kind: 'all' } | { kind: 'unclassified' };

export type Route =
  // `notice`: a one-time message the sign-in screen shows about how the inspector got there (the account
  // page unmounts the moment its "deleted" message appears, so the message rides on the route instead).
  | { screen: 'signIn'; notice?: 'accountDeleted' }
  // Home tab root: the landing screen after sign-in (today · continue · to-dos · this month).
  | { screen: 'home' }
  // Inspections tab root: every inspection on this device.
  | { screen: 'inspectionList' }
  | { screen: 'newInspection' }
  // Inspection Overview (areas + progress) of an open inspection.
  | { screen: 'inspection'; inspectionId: string }
  // Property Layout — the structure editing view: after creation and any time later.
  | { screen: 'propertyLayout'; inspectionId: string }
  // Inspection details: property / client / date / agreement / notes. `focus: 'agreement'` (Owner
  // 2026-10-07, 「去签协议」 from an AI card) brings the Inspection Agreement on screen on arrival.
  | { screen: 'inspectionDetails'; inspectionId: string; focus?: 'agreement' }
  // Path A — bulk import into the photo pool, then the processing choice (AI / manual).
  | { screen: 'bulkImport'; inspectionId: string }
  | { screen: 'importChoice'; inspectionId: string }
  // Photo Pool (Round 2 §S): area index first; an optional focus opens one area's grid / all photos.
  | { screen: 'photoPool'; inspectionId: string; focus?: PoolFocus }
  // `advancedJobId` (Advanced AI, 2026-09-13 §10): open the result of that job on arrival — the
  // deep-link target of a completion notification and of the AI Task Center. Consumed on arrival.
  | { screen: 'areaWorkspace'; inspectionId: string; areaId: string; itemSheetId: string | null; findingId?: string | null; advancedJobId?: string | null; focusDescriptor?: string | null }
  | { screen: 'findings'; inspectionId: string }
  | { screen: 'closeout'; inspectionId: string }
  // AI Task Center (2026-09-13 §12): every advanced job of one inspection, or of the account.
  | { screen: 'aiTasks'; inspectionId?: string | null }
  // Reports tab root + one inspection's report screen.
  | { screen: 'reports' }
  | { screen: 'report'; inspectionId: string }
  // Settings tab root + its sub-pages.
  | { screen: 'settings' }
  | { screen: 'settingsPage'; page: SettingsPage }
  // Development-only visual harness (src/app/FoundationGallery.tsx); never reachable in production.
  | { screen: 'foundationGallery' };

export interface NavState {
  stack: Route[];
  // The inspection the Reports tab pre-selects: the last one any route carried.
  lastInspectionId: string | null;
  // RETURN CONTEXT (Advanced AI, 2026-09-13 §11): the stack as it was before a detour to an AI
  // result. Back from the detour restores it wholesale — the kitchen sink stays the kitchen sink —
  // instead of unwinding to wherever the detour was pushed from. null = no detour in progress.
  detour: Route[] | null;
}

export const navStore = createStore<NavState>({ stack: [{ screen: 'home' }], lastInspectionId: null, detour: null });

function rememberInspection(prev: NavState, route: Route): string | null {
  return 'inspectionId' in route && typeof route.inspectionId === 'string' ? route.inspectionId : prev.lastInspectionId;
}

export function currentRoute(state: NavState): Route {
  return state.stack[state.stack.length - 1];
}

export function navigate(route: Route): void {
  navStore.set((prev) => ({ ...prev, stack: [...prev.stack, route], lastInspectionId: rememberInspection(prev, route) }));
}

export function replaceRoute(route: Route): void {
  navStore.set((prev) => ({
    ...prev,
    stack: [...prev.stack.slice(0, -1), route],
    lastInspectionId: rememberInspection(prev, route)
  }));
}

// Back: one step up the stack — or, when the current screen is the top of a detour, straight back
// to the stack the detour left (§11). Deeper pushes inside a detour unwind normally first.
export function goBack(): void {
  navStore.set((prev) => {
    if (prev.detour && prev.stack.length === prev.detour.length + 1) return { ...prev, stack: [...prev.detour], detour: null };
    return prev.stack.length > 1 ? { ...prev, stack: prev.stack.slice(0, -1) } : prev;
  });
}

export function resetNav(route: Route): void {
  navStore.set((prev) => ({ stack: [route], lastInspectionId: rememberInspection(prev, route), detour: null }));
}

// Replace the whole stack (used when a flow lands somewhere "back" must reach through a root).
export function resetStack(routes: Route[]): void {
  if (routes.length === 0) return;
  navStore.set((prev) => ({
    stack: [...routes],
    lastInspectionId: routes.reduce<string | null>((acc, route) => ('inspectionId' in route && typeof route.inspectionId === 'string' ? route.inspectionId : acc), prev.lastInspectionId),
    detour: null
  }));
}

// A DETOUR (§11): open a screen the inspector will come straight back from — the Advanced AI
// result reached through a notification or the AI Task Center — without losing where they were.
// A detour taken from inside a detour keeps the ORIGINAL return context.
export function openDetour(route: Route): void {
  navStore.set((prev) => ({
    stack: [...prev.stack, route],
    lastInspectionId: rememberInspection(prev, route),
    detour: prev.detour ?? [...prev.stack]
  }));
}

export function inDetour(state: NavState): boolean {
  return state.detour !== null;
}

// Which bottom tab a route belongs to; null = a screen outside the tab identity (sign-in,
// development gallery) where no tab is highlighted.
export function activeTabFor(route: Route): TabId | null {
  switch (route.screen) {
    case 'home':
      return 'home';
    case 'inspectionList':
    case 'newInspection':
    case 'inspection':
    case 'propertyLayout':
    case 'inspectionDetails':
    case 'bulkImport':
    case 'importChoice':
    case 'photoPool':
    case 'areaWorkspace':
    case 'findings':
    case 'closeout':
    case 'aiTasks':
      return 'inspections';
    case 'reports':
    case 'report':
      return 'reports';
    case 'settings':
    case 'settingsPage':
      return 'settings';
    case 'signIn':
    case 'foundationGallery':
      return null;
  }
}

// Screens that keep the global bottom tab bar. Deep field work (area · item · finding · AI scan ·
// photo pool · bulk import · closeout) and the new-inspection form hide it (task book §19).
const TAB_BAR_SCREENS: ReadonlySet<Route['screen']> = new Set<Route['screen']>([
  'home',
  'inspectionList',
  'inspection',
  'propertyLayout',
  'inspectionDetails',
  'findings',
  'aiTasks',
  'reports',
  'report',
  'settings',
  'settingsPage'
]);

export function showsTabBar(route: Route): boolean {
  return TAB_BAR_SCREENS.has(route.screen);
}

// Tab press: every tab is its own root.
export function switchTab(tab: TabId): void {
  switch (tab) {
    case 'home':
      resetNav({ screen: 'home' });
      return;
    case 'inspections':
      resetNav({ screen: 'inspectionList' });
      return;
    case 'reports':
      resetNav({ screen: 'reports' });
      return;
    case 'settings':
      resetNav({ screen: 'settings' });
      return;
  }
}
